<template>
	<div class="message-card">
		<!-- Row with icon and message title -->
		<div style="display: flex; align-items: center; gap: 8px">
			<div>
				<SvgIcon
					class="blexon-icon"
					type="mdi"
					:path="iconPath"
					:size="24"
					:style="`color: ${iconColor}`"
				/>
			</div>
			<div>
				<strong
					>{{ amountOfFeatures > 1 ? `(${amountOfFeatures}x) ` : ""
					}}{{ props.message.title }}<br
				/></strong>
				{{ toolDescription }}
			</div>
		</div>

		<!-- Full message text-->
		<div>
			<span v-html="props.message.shortDescription"></span>
		</div>

		<!-- Help link -->
		<div v-if="props.message.helpLink && props.message.helpLink.length > 0">
			<a :href="props.message.helpLink" target="_blank" rel="noreferrer"
				>{{ $t("panels.validationMessage.learnMoreLink") }}</a
			>
		</div>

		<!-- Actions -->
		<div class="actions-box" v-if="hasFeatures || canBeCorrected">
			<button
				v-if="hasFeatures"
				:class="isToggleOn ? 'blexon-button' : ''"
				@click="selectFeatures()"
				:title="$t('panels.validationMessage.showFeaturesTooltip')"
			>
				<SvgIcon type="mdi" :path="mdiEyeOutline" />
			</button>
			<button v-if="canBeCorrected || canOnlyBeCorrectedByAdmin" @click="correct()" :title="$t('panels.validationMessage.correctTooltip')">
				<SvgIcon type="mdi" :path="mdiCheckCircleOutline" />
			</button>
		</div>
	</div>

	<!-- Warning dialog: creating release cuts would lose certain features -->
	<div v-if="showReleaseCutsWarning" class="modal-overlay">
		<div class="modal">
			<h3 style="margin-bottom: 6px">{{ $t("panels.validationMessage.releaseCutsWarningTitle") }}</h3>
			<p>
				{{ $t("panels.validationMessage.releaseCutsWarningText1") }}
			</p>
			<p>
				{{ $t("panels.validationMessage.releaseCutsWarningText2") }}
			</p>
			<div style="display: flex; flex-direction: row-reverse; gap: 8px; margin-top: 12px">
				<button @click="confirmCreateReleaseCuts()" class="blexon-button">
					{{ $t("panels.validationMessage.createReleaseCutsButton") }}
				</button>
				<button @click="showReleaseCutsWarning = false">{{ $t("panels.validationMessage.cancelButton") }}</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useMainStore } from "@/store/mainStore";
import { IValidationMessage } from "@/model/IValidationMessage";
import SvgIcon from "@jamescoyle/vue-icon";
import {
	mdiAlertOutline,
	mdiInformationOutline,
	mdiCancel,
	mdiHelpCircleOutline,
	mdiEyeOutline,
	mdiCheckCircleOutline,
} from "@mdi/js";
import { computed, nextTick, ref } from "vue";
import { LoadingState, MessageType, ProcessType, Schweregrad, TaskType } from "@/model/Enums";
import { ITaskDto } from "@/model/ITaskDto";
import { FeatureType } from "@/model/FeatureType";

const props = defineProps<{ message: IValidationMessage }>();

const store = useMainStore();

// Feature types that are lost (and have to be redefined) when release cuts are created in the bend zone.
const featureTypesLostByReleaseCuts = [
	FeatureType.ScrewThread,
	FeatureType.Countersink,
	FeatureType.ExtrudedThread,
	FeatureType.CylinderHeadBoltHole,
];

const showReleaseCutsWarning = ref<boolean>(false);

const hasFeaturesLostByReleaseCuts = computed((): boolean => {
	return store.features.some((f) => featureTypesLostByReleaseCuts.includes(f.featureType) &&
		f.isConfirmed);
});

const hasFeatures = computed((): boolean => {
	return props.message.featureIds && props.message.featureIds.length > 0;
});

const canBeCorrected = computed((): boolean => {
	return (
		!store.article.isReadOnly &&
		props.message.messageType == MessageType.InnenAusschnittInBiegezone
	);
});

const canOnlyBeCorrectedByAdmin = computed((): boolean => {
	return (
		store.article.hasElevatedRights &&
		!store.article.isReadOnly &&
		props.message.messageType == MessageType.BiegeradiusUngleichBlechdicke
	);
});

const isToggleOn = computed((): boolean => {
	if (props.message.messageType === MessageType.InnenAusschnittInBiegezone) {
		return store.showBendzone;
	}
	return false;
});
const iconColor = computed((): string => {
	return props.message.severity === Schweregrad.Error
		? "#f54e42"
		: props.message.severity === Schweregrad.Warnung
			? "#f5b342"
			: "#4287f5";
});

const toolDescription = computed((): string => {
	if (hasFeatures && props.message.featureIds) {
		const feature = store.features.find((f) => f.featureId === props.message.featureIds[0]);
		if (feature !== undefined && feature.toolId !== null) {
			const parameter = store.screwThreadParameters.find((p) => p.toolId === feature.toolId);
			if (parameter !== undefined) {
				return parameter.bezeichnung;
			}
			const parameter2 = store.countersinkParameters.find((p) => p.toolId === feature.toolId);
			if (parameter2 !== undefined) {
				return parameter2.bezeichnung;
			}
			const parameter3 = store.extrudedThreadParameters.find(
				(p) => p.toolId === feature.toolId
			);
			if (parameter3 !== undefined) {
				return parameter3.bezeichnung;
			}
		}
	}
	return "";
});

const amountOfFeatures = computed((): number => {
	if (!props.message) return 0;
	if (props.message.severity == Schweregrad.Info) {
		return props.message?.featureIds?.length ?? 0;
	}
	return 0;
});

const selectFeatures = async () => {
	if (props.message.messageType === MessageType.InnenAusschnittInBiegezone) {
		store.showBendzone = !store.showBendzone;
		store.cam.setDisplayedBodies();
		return;
	}

	const newlySelected = store.features.filter((f) =>
		props.message.featureIds.includes(f.featureId)
	);
	newlySelected.forEach((s) => {
		store.addOrRemoveSelectedFeature(s.featureId, true);
	});

	// Draw a pulsating red dot on every affected face so small faces are easy to spot.
	const exportIds = newlySelected.flatMap((f) => f.exportIds);
	store.cam.pulseHighlightFaces(exportIds);

	nextTick(() => {
		if (props.message.messageType === MessageType.DurchzugErkannt) {
			store.targetProcessType = ProcessType.FormExtrudedThread;
		} else if (props.message.messageType === MessageType.GewindeErkannt) {
			store.targetProcessType = ProcessType.FormScrewThread;
		} else if (props.message.messageType === MessageType.SenkungErkannt) {
			store.targetProcessType = ProcessType.FormCountersink;
		}
	});
};

const correct = () => {
	console.log("Correcting features via validation message.", props.message);

	if (props.message.messageType === MessageType.BiegeradiusUngleichBlechdicke) {
		const tasks: ITaskDto[] = [];
		for (const featureId of props.message.featureIds) {
			const feature = store.features.find((f) => f.featureId === featureId);
			const originalExportIds = store.cam.getOriginalExportIds(feature!.exportIds);
			tasks.push({
				exportIds: originalExportIds,
				radius: 0, // Bei diesem Task wird automatisch die Blechdicke genommen, wenn nichts angegeben wird.
				innerRadius: 0,
				toolId: "",
				type: TaskType.ChangeBendRadius,
				isOnAnchorFace: true,
				newExportIds: [],
			});
		}
		store.cam.modifyFeatures(LoadingState.SettingFeature, tasks);
	}

	if (props.message.messageType === MessageType.InnenAusschnittInBiegezone) {
		// Gewinde, Senkungen, Durchzüge und Zylinderkopfschraubenlöcher gehen beim Erstellen
		// der Freischnitte verloren. In diesem Fall muss der Benutzer das erst bestätigen.
		if (hasFeaturesLostByReleaseCuts.value) {
			showReleaseCutsWarning.value = true;
		} else {
			createReleaseCuts();
		}
	}
};

const createReleaseCuts = () => {
	const tasks: ITaskDto[] = [];

	// Toggle on the bendzones
	if (!store.showBendzone) {
		selectFeatures();
	}
	for (const featureId of props.message.featureIds) {
		const feature = store.features.find((f) => f.featureId === featureId);
		console.log("exportIds", feature!.exportIds);
		const originalExportIds = store.cam.getOriginalExportIds(feature!.exportIds);
		console.log("originalExportIds", originalExportIds);
		tasks.push({
			exportIds: originalExportIds,
			radius: 0,
			innerRadius: 0,
			toolId: "",
			type: TaskType.CreateReleaseCuts,
			isOnAnchorFace: true,
			newExportIds: [],
		});
	}
	// Toggle off the bendzones
	selectFeatures();

	store.cam.modifyFeatures(LoadingState.SettingFeature, tasks);
	store.repeatBendSimulation = true;
};

const confirmCreateReleaseCuts = () => {
	showReleaseCutsWarning.value = false;
	createReleaseCuts();
};

const iconPath = computed((): string => {
	switch (props.message.severity) {
		case Schweregrad.Info: {
			return mdiInformationOutline;
		}
		case Schweregrad.Warnung: {
			return mdiAlertOutline;
		}
		case Schweregrad.Error: {
			return mdiCancel;
		}
		default: {
			return mdiHelpCircleOutline;
		}
	}
});
</script>

<style scoped>
.message-card {
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 12px;
	background-color: white;
	border: 1px solid var(--default-border-color);
	border-radius: 4px;
}

.blexon-icon {
	color: var(--blexon-color);
}

.actions-box {
	display: flex;
	align-items: center;
	justify-content: space-around;
	margin-top: 8px;
}
</style>
